import { useState, useCallback, useEffect, useRef } from 'react';
import { useConfig } from '../contexts/ConfigContext';
import Request from '../services/fetchDataService';
import { mutate } from 'swr';

export function useSecureRequest() {
  const { config, updateConfig } = useConfig();
  const [loading, setLoading] = useState(false);
  const tokenRef = useRef(config.token);

  // Keep ref updated to detect token changes
  useEffect(() => {
    tokenRef.current = config.token;
  }, [config.token]);

  const securePost = useCallback(async (url: string, data: any) => {
    setLoading(true);
    updateConfig({ showProgress: true });
    
    // Helper to wait for token refresh
    const waitForTokenRefresh = async () => {
      const startToken = tokenRef.current;
      let retries = 0;
      // Poll every 100ms for up to 3 seconds
      while (tokenRef.current === startToken && retries < 30) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }
      return tokenRef.current;
    };

    try {
      const response = await Request.post(url, config.key, data);
      return response;
    } catch (error: any) {
      if (error?.status === 240) {
        // Token expired. Trigger global refresh via SWR key mutation.
        await mutate(
            (k: any) => Array.isArray(k) && typeof k[0] === 'string' && k[0].includes('/security/auth/'),
            undefined,
            { revalidate: true }
        );

        // Wait for the new token to propagate to ConfigContext
        const newToken = await waitForTokenRefresh();

        // Retry the request
        const retryData = { ...data };
        if (retryData.csrfToken) {
          retryData.csrfToken = newToken;
        }

        return await Request.post(url, config.key, retryData);
      }
      throw error;
    } finally {
      setLoading(false);
      updateConfig({ showProgress: false });
    }
  }, [config.key, updateConfig]);

  return { securePost, loading };
}
